/**
 * Canonical condition_key strings used in the verifications table.
 *
 * The condition_key identifies WHAT is being checked.
 * The testing_day column identifies WHICH DAY the check applies to.
 *
 * Together they form a unique tuple per tester.
 *
 * For one-time tests (Setup, Sharing, Challenge Joins), testing_day is
 * conventionally 1 — they're not day-specific, but the unique constraint
 * needs a value, so we always use 1.
 *
 * For per-day tests (Daily Profile, Workout Sync, Challenge Progress),
 * testing_day is the day the check is "for" (e.g., the day whose data
 * is being verified or the day the workout happened). The same key
 * with different testing_day values represents different days of the
 * same condition.
 */

// One-time test keys (testing_day = 1 by convention)
export const SETUP_SYNC = "setup_sync";

/**
 * One-time sharing tests. Single source of truth: this list drives the tester
 * section, the completion-percentage denominator in engagement.ts, and the
 * Coverage Matrix "Sharing — any" rollup.
 *
 * Direct-message sharing is NOT in this release, so the two DM tests
 * ("sharing_workout_dm", "sharing_map_dm") were removed on 2026-08-17. They
 * were generating tickets against an already-logged DM crash, and because the
 * section only hides once every test is verified, leaving them in parked an
 * incomplete section on every dashboard for the whole window.
 *
 * To restore when DM ships: re-add both keys here and their entries in
 * SharingTestsSection's TESTS array. Everything downstream follows.
 */
export const SHARING_KEYS = ["sharing_workout_feed", "sharing_map_feed"] as const;
export type SharingKey = (typeof SHARING_KEYS)[number];

// Per-day daily test keys. These match the condition_config table — the
// admin can add or remove rows without touching code.
// (These constants are the seed values; the runtime list comes from
// useAppConfig().conditionConfig and may differ if Amanda adds/removes.)
export const DAILY_PROFILE_KEYS = [
  "daily_distance",
  "daily_steps",
  "daily_active_minutes",
  "daily_calories",
] as const;
export type DailyProfileKey = (typeof DAILY_PROFILE_KEYS)[number];

export const WORKOUT_SYNC = "workout_all_fields";

/** Build the per-tester challenge join key (one-time, testing_day=1). */
export function challengeJoinKey(challenge: string): string {
  return `challenge_join_${slugify(challenge)}`;
}

/** Build the per-tester per-day challenge progress key. */
export function challengeProgressKey(challenge: string): string {
  return `challenge_${slugify(challenge)}`;
}

/** Lowercase + collapse non-alphanumerics to a single underscore. */
export function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}
